/**
 * Property conflict resolution logic for the CRM merge wizard.
 * Categorises properties from two CrmObjects into matching, auto-resolved, conflicting, multi-value, and display-name-conflict buckets.
 */

import { useMemo } from 'react';

import type {
  PropertySchemaMap,
  PropertyTypeSchema,
} from '@/lib/crm/hooks/usePropertySchema';
import {
  DISPLAY_NAME_FIELDS,
  SYSTEM_FIELDS,
} from '@/lib/crm/record/registry/fieldConstants';
import type { CrmObject, PropertyValue } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type {
  MultiPropertyResolution,
  PropertyResolutionResult,
  SinglePropertyResolution,
} from './types';

/** Returns true when a property value is considered empty for merge purposes. */
export function isMergeValueEmpty(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

// JSON.stringify deep-equals — handles nested objects like phone numbers per issue's Technical Notes
function valuesAreEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function coerceToArray(value: unknown): unknown[] {
  if (isMergeValueEmpty(value)) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
}

/**
 * Pure function that categorises all properties from two CrmObjects.
 * Can be called directly in unit tests without a React context.
 */
export function computeMergePropertyResolution(
  canonical: CrmObject,
  source: CrmObject,
  schema: PropertySchemaMap
): PropertyResolutionResult {
  const matching: SinglePropertyResolution[] = [];
  const autoResolved: SinglePropertyResolution[] = [];
  const conflicts: SinglePropertyResolution[] = [];
  const multiValue: MultiPropertyResolution[] = [];
  const displayNameConflicts: SinglePropertyResolution[] = [];

  for (const propertyName of Object.keys(schema)) {
    if (SYSTEM_FIELDS.has(propertyName)) {
      continue;
    }

    const propSchema: PropertyTypeSchema | undefined = schema[propertyName];
    if (propSchema === undefined) {
      continue;
    }

    if (propSchema.value_write_access !== 'all') {
      continue;
    }
    if (propSchema.value_update_access === 'none') {
      continue;
    }
    if (propSchema.value_read_access !== 'all') {
      continue;
    }

    const canonicalRaw = getPropertyValue<unknown>(
      canonical.properties?.[propertyName]
    );
    const sourceRaw = getPropertyValue<unknown>(
      source.properties?.[propertyName]
    );
    const canonicalWrapper = canonical.properties?.[propertyName] ?? null;
    const sourceWrapper = source.properties?.[propertyName] ?? null;

    if (propSchema.is_multi === true) {
      const canonicalArr = coerceToArray(canonicalRaw);
      const sourceArr = coerceToArray(sourceRaw);

      const canonicalItems: Array<{
        value: PropertyValue;
        keep: boolean;
        stableKey: string;
      }> = [];
      for (const item of canonicalArr) {
        canonicalItems.push({
          value: { value: item },
          keep: true,
          stableKey: JSON.stringify(item),
        });
      }

      const canonicalSet = new Set(
        canonicalArr.map(item => JSON.stringify(item))
      );
      const sourceItems: Array<{
        value: PropertyValue;
        keep: boolean;
        stableKey: string;
      }> = [];
      for (const item of sourceArr) {
        if (!canonicalSet.has(JSON.stringify(item))) {
          sourceItems.push({
            value: { value: item },
            keep: true,
            stableKey: JSON.stringify(item),
          });
        }
      }

      if (canonicalItems.length === 0 && sourceItems.length === 0) {
        continue;
      }

      multiValue.push({ propertyName, canonicalItems, sourceItems });
      continue;
    }

    const canonicalEmpty = isMergeValueEmpty(canonicalRaw);
    const sourceEmpty = isMergeValueEmpty(sourceRaw);

    if (canonicalEmpty && sourceEmpty) {
      continue;
    }

    const resolution: SinglePropertyResolution = {
      propertyName,
      winner: 'canonical',
      canonicalValue: canonicalWrapper,
      sourceValue: sourceWrapper,
    };

    if (
      !canonicalEmpty &&
      !sourceEmpty &&
      valuesAreEqual(canonicalRaw, sourceRaw)
    ) {
      matching.push(resolution);
      continue;
    }

    if (canonicalEmpty && !sourceEmpty) {
      resolution.winner = 'source';
      autoResolved.push(resolution);
      continue;
    }

    if (!canonicalEmpty && sourceEmpty) {
      autoResolved.push(resolution);
      continue;
    }

    conflicts.push(resolution);
    if (DISPLAY_NAME_FIELDS.has(propertyName)) {
      displayNameConflicts.push(resolution);
    }
  }

  return {
    matching,
    autoResolved,
    conflicts,
    multiValue,
    displayNameConflicts,
  };
}

/** Hook wrapper — memoises the pure resolution computation across renders. */
export function useMergePropertyResolution(
  canonical: CrmObject,
  source: CrmObject,
  schema: PropertySchemaMap
): PropertyResolutionResult {
  return useMemo(
    () => computeMergePropertyResolution(canonical, source, schema),
    [canonical, source, schema]
  );
}
